/**
 * DonationItem
 * Flutter source: wallet_screen.dart - _donationOperationItem()
 *
 * Displays roadmap donation transactions.
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AdminUserAvatar } from './AdminUserAvatar';

interface DonationItemProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  isFromAdmin?: boolean;
}

export const DonationItem: React.FC<DonationItemProps> = ({
  transaction,
  currentUserId,
  isFromAdmin = false,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();

  const isCurrentUser = transaction.usersInfo.userId === currentUserId;
  const title = isCurrentUser ? l10n.wallet.youDonatedFor + ' ' : l10n.wallet.someoneDonatedFor + ' ';

  const subTitle = `${MathUtils.roundTwoDecimals(transaction.walletInfo?.money ?? 0)} ${transaction.walletInfo?.currency ?? ''} · ${dateFormatters.formatDate(transaction.createdDate, true)}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
      <div
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          backgroundColor: theme.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AssetIcon
          path="/assets/images/roadmap.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.text.primary}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
          <span>{title}</span>
          <span style={{ color: theme.exclusive, fontWeight: responsive.fontWeight.semibold }}>
            {l10n.wallet.idea}
          </span>
        </div>
        <div style={{ color: theme.text.secondary, fontSize: responsive.font.xs, marginTop: responsive.gap.xs }}>
          {subTitle}
        </div>
      </div>

      {isFromAdmin && <AdminUserAvatar userId={transaction.usersInfo.userId} />}
    </div>
  );
};
